import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  AlertCircle,
  Copy,
  Loader2,
  Search,
  Shield,
  Server,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { v2Api } from '@/lib/api';
import type {
  ExerciseRunResponse,
  ExerciseRunUserSearchItem,
  ParticipantAccessMode,
} from '@/lib/api/v2';
import type { InjectRole } from '@/lib/api/v2';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const ACCESS_MODE_OPTIONS: {
  value: ParticipantAccessMode;
  label: string;
  short: string;
}[] = [
  { value: 'vm_only', label: 'VM only — console / lab infrastructure', short: 'VM only' },
  { value: 'ttx_only', label: 'TTX only — injects / narrative', short: 'TTX only' },
  { value: 'combined', label: 'VM + TTX — both', short: 'VM + TTX' },
];

interface ManageParticipantsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioName: string;
  /** Exercise (scenario) UUID — Django ``ExerciseModel`` id. */
  scenarioId: string;
  /** Relational exercise run primary key (``ExerciseActiveModel``). */
  runId: number;
  /** Optional callback to refresh the parent run UI after participant changes. */
  onRunUpdated?: () => void;
  /** When false, only ``vm_only`` is valid (TTX paths disabled for this exercise). */
  exerciseTtxEnabled?: boolean;
}

export function ManageParticipantsDialog({
  open,
  onOpenChange,
  scenarioName,
  scenarioId,
  runId,
  onRunUpdated,
  exerciseTtxEnabled,
}: ManageParticipantsDialogProps) {
  const [run, setRun] = useState<ExerciseRunResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleOptions, setRoleOptions] = useState<InjectRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [accessLocked, setAccessLocked] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ExerciseRunUserSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ExerciseRunUserSearchItem | null>(null);
  const [addRoleId, setAddRoleId] = useState<string>('');
  const [selectedComputeInstanceId, setSelectedComputeInstanceId] = useState<string>('');
  // TTX-first by default (VM is opt-in via vm_required teams).
  const [accessMode, setAccessMode] = useState<ParticipantAccessMode>('ttx_only');

  const accessModeOptions = useMemo(() => {
    if (exerciseTtxEnabled === false) {
      return ACCESS_MODE_OPTIONS.filter((o) => o.value === 'vm_only');
    }
    return ACCESS_MODE_OPTIONS;
  }, [exerciseTtxEnabled]);

  useEffect(() => {
    if (exerciseTtxEnabled === false && accessMode !== 'vm_only') {
      setAccessMode('vm_only');
    }
  }, [exerciseTtxEnabled, accessMode]);

  /** Changing access level restarts the role + user steps. */
  useEffect(() => {
    if (!open) return;
    setAddRoleId('');
    setSelectedComputeInstanceId('');
    setSelectedUser(null);
    setUserSearchQuery('');
    setSearchResults([]);
  }, [accessMode, open]);

  const [adding, setAdding] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  /** Reset local form state when the dialog closes */
  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setUserSearchQuery('');
      setSearchResults([]);
      setAddRoleId('');
      setSelectedComputeInstanceId('');
      setAccessMode('ttx_only');
      setAccessLocked(false);
      setRoleOptions([]);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchResults([]);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const fetchRun = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.dashboard.exerciseRuns.get(scenarioId, runId);
      try {
        const list = await v2Api.dashboard.exerciseRuns.listByExerciseId(scenarioId);
        if (Array.isArray(list)) {
          const d = list.find((r) => r.id === data.id);
          if (d) {
            setRun({
              ...data,
              ...d,
              participants:
                d.participants && d.participants.length > 0 ? d.participants : data.participants,
            });
            return;
          }
        }
      } catch {
        /* optional merge */
      }
      setRun(data);
    } catch (e: unknown) {
      let msg: string | null = null;
      if (e && typeof e === 'object' && 'response' in e) {
        const res = (e as { response?: { status?: number } }).response;
        if (res?.status !== 404) {
          msg = e instanceof Error ? e.message : String(e);
        }
      } else {
        msg = e instanceof Error ? e.message : (e != null ? String(e) : null);
      }
      setError(msg);
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [scenarioId, runId]);

  useEffect(() => {
    if (open) {
      fetchRun();
    }
  }, [open, fetchRun]);

  /** Load teams (roles) for the selected access level — order: access → role → user */
  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    (async () => {
      setRolesLoading(true);
      try {
        const res = await v2Api.dashboard.exerciseRuns.participantRoleOptions(
          scenarioId,
          runId,
          accessMode,
        );
        if (cancelled) return;
        if (res.access_mode_locked && res.effective_access_mode && res.effective_access_mode !== accessMode) {
          setAccessMode(res.effective_access_mode);
        }
        setAccessLocked(Boolean(res.access_mode_locked));
        setRoleOptions(Array.isArray(res.roles) ? res.roles : []);
      } catch {
        if (!cancelled) {
          setRoleOptions([]);
          setAccessLocked(false);
        }
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, scenarioId, runId, accessMode]);

  useEffect(() => {
    if (!open || !run) return;
    if (!addRoleId) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const q = userSearchQuery.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const list = await v2Api.dashboard.exerciseRuns.participants.searchUsers(
          scenarioId,
          runId,
          q,
          true,
          addRoleId,
        );
        setSearchResults(Array.isArray(list) ? list : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [open, run, userSearchQuery, addRoleId, runId, scenarioId]);

  const handleAddParticipant = async () => {
    if (!selectedUser) return;
    const rid = addRoleId ? parseInt(addRoleId, 10) : NaN;
    if (!Number.isFinite(rid)) return;
    setAdding(true);
    setError(null);
    try {
      const payload: {
        user_id: number;
        role_id: number;
        access_mode: ParticipantAccessMode;
        compute_instance_id?: string;
      } = {
        user_id: selectedUser.id,
        role_id: rid,
        // Backend validation requires vm_only when TTX is disabled.
        access_mode: exerciseTtxEnabled === false ? 'vm_only' : accessMode,
      };

      const effectiveAccessMode: ParticipantAccessMode =
        exerciseTtxEnabled === false ? 'vm_only' : accessMode;

      if (effectiveAccessMode !== 'ttx_only') {
        // For VM modes, user must choose exactly 1 VM.
        if (!selectedComputeInstanceId) {
          throw new Error('Please select an available VM to assign.');
        }
        // Extra safety: block adding if the instance has become taken.
        const currentTaken = new Set(
          (run?.participants ?? [])
            .map((p) => p.assigned_compute_instance_id)
            .filter(Boolean)
            .map((id) => String(id)),
        );
        if (currentTaken.has(String(selectedComputeInstanceId))) {
          throw new Error('Selected VM/instance is already assigned.');
        }
        payload.compute_instance_id = selectedComputeInstanceId;
      }

      const added = await v2Api.dashboard.exerciseRuns.participants.add(scenarioId, runId, payload);
      setRun((prev) =>
        prev
          ? {
              ...prev,
              participants: [...(prev.participants ?? []), added],
            }
          : prev,
      );
      setSelectedUser(null);
      setAddRoleId('');
      setSelectedComputeInstanceId('');
      setUserSearchQuery('');
      setSearchResults([]);
      toast.success('Participant added to run');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to add participant';
      setError(msg);
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setUserSearchQuery('');
    setSearchResults([]);
  };

  const participantCount = run?.participants?.length ?? 0;
  const statusLabel = run?.status_display ?? run?.status ?? '—';
  const effectiveAccessMode: ParticipantAccessMode =
    exerciseTtxEnabled === false ? 'vm_only' : accessMode;
  const requiresVmSelection = effectiveAccessMode !== 'ttx_only' && Boolean(addRoleId);
  const canSubmit = Boolean(selectedUser && addRoleId && !adding && (!requiresVmSelection || selectedComputeInstanceId));
  const hasRole = Boolean(addRoleId);
  const hasUser = Boolean(selectedUser);
  const selectedRoleName = roleOptions.find((r) => String(r.id) === addRoleId)?.name ?? '';
  const accessShortLabel =
    ACCESS_MODE_OPTIONS.find((o) => o.value === accessMode)?.short ?? accessMode;
  const selectedUserLabel = selectedUser
    ? selectedUser.full_name || selectedUser.email || `User #${selectedUser.id}`
    : '';

  const selectedTeamId = useMemo(() => {
    if (!addRoleId) return null;
    const n = Number(addRoleId);
    return Number.isFinite(n) ? n : null;
  }, [addRoleId]);

  const vmInstancesForSelectedRole = useMemo(() => {
    const instances = run?.scenario?.infrastructure?.instances ?? [];
    if (!selectedTeamId) return [];
    return instances.filter((i) => Number(i.team_id) === selectedTeamId);
  }, [run?.scenario?.infrastructure?.instances, selectedTeamId]);

  const assignedParticipantByInstanceId = useMemo(() => {
    const out: Record<string, string> = {};
    const parts = run?.participants ?? [];
    for (const p of parts) {
      const instanceId = p.assigned_compute_instance_id;
      if (!instanceId) continue;
      const name =
        p.user?.full_name ??
        p.user_full_name ??
        p.user?.email ??
        p.user_email ??
        (p.user?.id ? `User #${p.user.id}` : `User #${p.user_id}`);
      out[String(instanceId)] = name || `User #${p.user_id}`;
    }
    return out;
  }, [run?.participants]);

  const takenInstanceIds = useMemo(() => {
    const out = new Set<string>();
    const parts = run?.participants ?? [];
    for (const p of parts) {
      if (p.assigned_compute_instance_id) out.add(String(p.assigned_compute_instance_id));
    }
    return out;
  }, [run?.participants]);

  const availableVmInstancesForSelectedRole = useMemo(() => {
    return vmInstancesForSelectedRole.filter((inst) => !takenInstanceIds.has(String(inst.id)));
  }, [vmInstancesForSelectedRole, takenInstanceIds]);

  // In VM-only exercises, ensure VM selection is visible even if accessMode state is briefly inconsistent.
  const showVmAssignmentReadOnly =
    (exerciseTtxEnabled === false || accessMode !== 'ttx_only') && Boolean(addRoleId);

  const copyScenarioId = async () => {
    try {
      await navigator.clipboard.writeText(scenarioId);
      toast.success('Scenario ID copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[min(84vh,720px)] w-[calc(100vw-1rem)] max-w-2xl flex-col gap-0 overflow-hidden border-border/80 p-0 shadow-2xl sm:w-full">
          {/* Header */}
          <div className="relative border-b bg-gradient-to-br from-muted/40 via-background to-background px-4 pb-3 pt-4">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/60 via-primary/30 to-transparent" />
            <DialogHeader className="space-y-3 text-left">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <DialogTitle className="text-xl font-semibold tracking-tight sm:text-2xl">
                    Manage participants
                  </DialogTitle>
                  <p className="text-muted-foreground text-sm font-medium leading-snug">{scenarioName}</p>
                </div>
                {run && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-xs font-normal">
                      Run #{runId}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-normal">
                      {statusLabel}
                    </Badge>
                    <Badge variant="default" className="gap-1 pl-2 pr-2.5 text-xs font-normal">
                      <Users className="h-3 w-3 opacity-80" />
                      {participantCount}
                    </Badge>
                  </div>
                )}
              </div>
              <DialogDescription className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                Pick an <span className="text-foreground/90 font-medium">access level</span>, a{' '}
                <span className="text-foreground/90 font-medium">role</span>, then select a{' '}
                <span className="text-foreground/90 font-medium">user</span>.
                {exerciseTtxEnabled === false ? (
                  <> You must also choose an <span className="text-foreground/90 font-medium">Instance / VM</span>.</>
                ) : null}
              </DialogDescription>
            </DialogHeader>
          </div>

          {loading && !run && (
            <div className="flex flex-col items-center justify-center gap-3 px-4 py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary/70" />
              <p className="text-muted-foreground text-sm">Loading run and participants…</p>
            </div>
          )}

          {error && (
            <div className="mx-6 mt-4 flex gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="min-w-0 flex-1 leading-snug">{error}</p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/15"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {run && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="custom-scrollbar flex-1 space-y-3 overflow-y-auto px-4 py-4">
                {/* Run context */}
                <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">Exercise run</span>
                  <span className="max-w-[220px] truncate sm:max-w-md" title={run.name}>
                    {run.name}
                  </span>
                </div>

                {/* Add participant */}
                <Card className="border-border/80 shadow-sm">
                  <CardHeader className="space-y-1 pb-4">
                    <CardTitle className="flex items-center gap-2 text-base font-semibold">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <UserPlus className="h-4 w-4" />
                      </span>
                      Add participant
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed">
                      <p className="text-muted-foreground text-[11px] leading-snug">
                        Select <span className="text-foreground/90 font-medium">{accessShortLabel}</span>, then a{' '}
                        <span className="text-foreground/90 font-medium">role</span>, then a <span className="text-foreground/90 font-medium">user</span>
                        {showVmAssignmentReadOnly ? (
                          <>
                            {' '}and an <span className="text-foreground/90 font-medium">Instance / VM</span>.
                          </>
                        ) : (
                          '.'
                        )}
                      </p>
                      <p className="mt-1 text-muted-foreground text-[11px] leading-snug">
                        Selected: <span className="text-foreground/90 font-medium">{accessShortLabel}</span>
                        {' '}· Role: <span className="text-foreground/90 font-medium">{selectedRoleName || '—'}</span>
                        {' '}· User: <span className="text-foreground/90 font-medium">{selectedUserLabel || '—'}</span>
                      </p>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <form
                      className="space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAddParticipant();
                      }}
                    >
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="access-mode-select"
                          className="flex items-center gap-1.5 text-xs font-medium text-foreground"
                        >
                          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                          Access level
                        </Label>
                        {accessLocked ? (
                          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                            <Badge variant="secondary" className="font-normal">
                              {ACCESS_MODE_OPTIONS.find((o) => o.value === accessMode)?.label ?? accessMode}
                            </Badge>
                            <span className="text-muted-foreground text-xs">
                              Fixed for this exercise (no VM teams / instances mapped).
                            </span>
                          </div>
                        ) : (
                          <Select
                            id="access-mode-select"
                            value={accessMode}
                            onChange={(e) => setAccessMode(e.target.value as ParticipantAccessMode)}
                            className="w-full"
                            disabled={rolesLoading}
                          >
                            {accessModeOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                        )}
                        {exerciseTtxEnabled === false ? (
                          <p className="text-[11px] leading-snug text-amber-600 dark:text-amber-500">
                            TTX is disabled for this exercise — only VM access is available.
                          </p>
                        ) : (
                          <p className="text-muted-foreground text-[11px] leading-snug">
                            VM = lab/console · TTX = injects · Combined = both. Roles shown below match this choice.
                          </p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="add-role-select" className="text-xs font-medium text-foreground">
                          Role
                        </Label>
                        <Select
                          id="add-role-select"
                          value={addRoleId}
                          onChange={(e) => {
                            clearSelectedUser();
                            setAddRoleId(e.target.value);
                            setSelectedComputeInstanceId('');
                          }}
                          className="w-full"
                          disabled={rolesLoading}
                        >
                          <option value="">
                            {rolesLoading ? 'Loading roles…' : 'Select role…'}
                          </option>
                          {roleOptions.map((r) => (
                            <option key={r.id} value={String(r.id)}>
                              {r.name}
                            </option>
                          ))}
                        </Select>
                        {rolesLoading ? (
                          <p className="text-muted-foreground text-[11px] leading-snug">Updating roles for this access…</p>
                        ) : roleOptions.length === 0 ? (
                          <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-500">
                            No roles available for this access level. For VM or combined, map VMs to exercise teams; for
                            TTX-only, add team mappings or use an exercise with TTX enabled.
                          </p>
                        ) : (
                          <p className="text-muted-foreground text-[11px] leading-snug">
                            Users must match this role in their account.
                          </p>
                        )}
                      </div>
                    </div>

                    {showVmAssignmentReadOnly && (
                      <div className="space-y-2 rounded-lg border bg-muted/20 px-3 py-2 sm:px-4">
                        <div className="flex items-center justify-between gap-2">
                          <p className="flex items-center gap-2 text-xs font-medium text-foreground">
                            <Server className="h-3.5 w-3.5 text-muted-foreground" />
                            Instance / VM
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="select-compute-instance" className="text-xs font-medium text-foreground">
                            Instance / VM
                            <span className="text-destructive">*</span>
                          </Label>
                          <Select
                            id="select-compute-instance"
                            value={selectedComputeInstanceId}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v && takenInstanceIds.has(String(v))) {
                                // Should be impossible due to disabled options, but keep UX safe.
                                toast.error('This VM/instance is already assigned.');
                                return;
                              }
                              setSelectedComputeInstanceId(v);
                            }}
                            disabled={rolesLoading || vmInstancesForSelectedRole.length === 0}
                            className="w-full"
                          >
                            <option value="">
                              {vmInstancesForSelectedRole.length === 0 ? 'No VMs mapped' : 'Select an unassigned VM…'}
                            </option>
                            {vmInstancesForSelectedRole.map((inst) => {
                              const iid = String(inst.id);
                              const assignee = assignedParticipantByInstanceId[iid];
                              const isTaken = takenInstanceIds.has(iid);

                              return (
                                <option key={inst.id} value={iid} disabled={isTaken}>
                                  {inst.name}
                                  {assignee ? (isTaken ? ` (assigned: ${assignee})` : ' (this)') : ' (unassigned)'}
                                </option>
                              );
                            })}
                          </Select>
                          {vmInstancesForSelectedRole.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground leading-snug">
                              No instances are mapped to this role. Create/assign instances under the exercise infrastructure first.
                            </p>
                          ) : !selectedComputeInstanceId ? (
                            <p className="text-[11px] text-muted-foreground leading-snug">
                              Select an unassigned VM to assign it to this participant.
                            </p>
                          ) : (
                            <p className="text-[11px] text-muted-foreground leading-snug">
                              Assigned to:{' '}
                              {assignedParticipantByInstanceId[String(selectedComputeInstanceId)] ?? 'Unassigned'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="space-y-1.5" ref={searchContainerRef}>
                      <Label htmlFor="participant-search" className="text-xs font-medium text-foreground">
                        User
                      </Label>
                      {selectedUser ? (
                        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/35 bg-primary/[0.06] px-3 py-2">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                            {(selectedUser.full_name || selectedUser.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {selectedUser.full_name || selectedUser.email || `User #${selectedUser.id}`}
                            </p>
                            {selectedUser.email && selectedUser.full_name && (
                              <p className="truncate text-xs text-muted-foreground">{selectedUser.email}</p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="shrink-0"
                            onClick={clearSelectedUser}
                          >
                            Change
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="participant-search"
                            type="search"
                            placeholder={
                              rolesLoading
                                ? 'Loading roles…'
                                : addRoleId
                                  ? 'Type name or email…'
                                  : 'Select a role first'
                            }
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="h-10 pl-9 pr-10"
                            autoComplete="off"
                            aria-describedby="search-hint"
                            disabled={!addRoleId || rolesLoading}
                          />
                          {userSearchQuery ? (
                            <button
                              type="button"
                              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => setUserSearchQuery('')}
                              aria-label="Clear search"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          ) : null}

                          {addRoleId && userSearchQuery.trim().length >= MIN_SEARCH_LENGTH && (
                            <div
                              className="absolute left-0 right-0 top-full z-40 mt-1 max-h-56 overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-popover"
                              role="listbox"
                              aria-label="Search results"
                            >
                              {searchLoading ? (
                                <div className="flex items-center justify-center py-10">
                                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                                </div>
                              ) : searchResults.length === 0 ? (
                                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                                  No users found
                                </p>
                              ) : (
                                <ul className="max-h-56 overflow-y-auto py-1">
                                  {searchResults.map((u) => (
                                    <li key={u.id}>
                                      <button
                                        type="button"
                                        role="option"
                                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
                                        onClick={() => {
                                          setSelectedUser(u);
                                          setUserSearchQuery('');
                                          setSearchResults([]);
                                        }}
                                      >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                                          {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                          <span className="block truncate font-medium">
                                            {u.full_name || u.email || `User #${u.id}`}
                                          </span>
                                          {u.email && u.full_name && (
                                            <span className="block truncate text-xs text-muted-foreground">
                                              {u.email}
                                            </span>
                                          )}
                                        </span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {!addRoleId && !rolesLoading && (
                        <p id="search-hint" className="text-muted-foreground text-xs">
                          Choose a role to search for users.
                        </p>
                      )}
                      {addRoleId &&
                        userSearchQuery.trim().length > 0 &&
                        userSearchQuery.trim().length < MIN_SEARCH_LENGTH && (
                          <p id="search-hint" className="text-muted-foreground text-xs">
                            Enter at least {MIN_SEARCH_LENGTH} characters.
                          </p>
                        )}
                    </div>

                    <Separator className="bg-border/60" />

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-muted-foreground text-xs sm:max-w-sm">
                        {canSubmit
                          ? 'Ready to add this user with the selected access, role, and account.'
                          : 'Pick access level, role, then search and select a user.'}
                      </p>
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full shrink-0 sm:w-auto sm:min-w-[160px]"
                        disabled={!canSubmit}
                      >
                        {adding ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Adding…
                          </>
                        ) : (
                          <>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Add Participant
                          </>
                        )}
                      </Button>
                    </div>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Footer */}
              <div className="border-t bg-muted/20 px-4 py-2">
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="flex items-center gap-1.5">
                      <span className="shrink-0">Scenario ID</span>
                      <code
                        className="max-w-[min(100%,14rem)] truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground/80"
                        title={scenarioId}
                      >
                        {scenarioId.slice(0, 8)}…
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                        onClick={copyScenarioId}
                        aria-label="Copy full scenario ID"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground/90">
                      Run #{runId}
                    </span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-8 shrink-0 text-xs" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
